// src/utils/electrical-calibration-profile.js
//
// CATÁLOGO ELÉCTRICO — Fase 1 del módulo de certificados multi-magnitud.
// Define las funciones del multímetro/amperímetro/ohmímetro, el parseo de
// valores con multiplicador (1K, 5M…), la convención de signo del error y los
// datos del patrón. Es la fuente única para la captura (F2) y el cálculo (F3).
//
// Basado en la planilla real R-DT-PC-...-MULTIMETROS Rev02 (8 funciones,
// 6 puntos, 1 lectura patrón + 1 instrumento por punto, sin series/histéresis).

// ── Las 8 funciones (2 grupos, como en el Excel) ────────────────────────────
export const ELECTRICAL_FUNCTIONS = [
  // Grupo 1
  { key: "vac",        group: "ac", label: "Tensión CA",         unit: "V",  unitLabel: "[V]"  },
  { key: "resistance", group: "ac", label: "Resistencia",        unit: "Ω",  unitLabel: "[Ω]", allowMultiplier: true },
  { key: "aac",        group: "ac", label: "Corriente CA",       unit: "A",  unitLabel: "[A]"  },
  { key: "maac",       group: "ac", label: "Corriente CA (mA)",  unit: "mA", unitLabel: "[mA]" },
  { key: "frequency",  group: "ac", label: "Frecuencia",         unit: "Hz", unitLabel: "[Hz]" },
  // Grupo 2
  { key: "vdc",        group: "dc", label: "Tensión CC",         unit: "V",  unitLabel: "[V]"  },
  { key: "adc",        group: "dc", label: "Corriente CC",       unit: "A",  unitLabel: "[A]"  },
  { key: "madc",       group: "dc", label: "Corriente CC (mA)",  unit: "mA", unitLabel: "[mA]" },
];

export const ELECTRICAL_FUNCTION_BY_KEY = Object.fromEntries(
  ELECTRICAL_FUNCTIONS.map((f) => [f.key, f])
);

// Plantilla de calibración del multímetro (las 8 funciones, 6 puntos c/u).
export const MULTIMETER_TEMPLATE = {
  code: "R-DT-PC-II-RR",
  revision: "02",
  family: "electrical",
  title: "Calibración de Multímetros, Amperímetros y Ohmímetros",
  defaultPointCount: 6,
  functions: ELECTRICAL_FUNCTIONS.map((f) => f.key),
};

// Datos del patrón del laboratorio (debe coincidir con el seed de la migración).
export const ELECTRICAL_STANDARD = {
  code: "E-012",
  name: "Clamp Multimeter Calibrator",
  brand: "GFUVE",
  model: "GF6018A",
  serial: "20250406",
  certificateNo: "20250406",
  calibrationDate: "2025-05-28",
  dueDate: "2027-05-27",
  accuracyClass: 0.05, // % — clase de exactitud (test report de fábrica)
  traceability: "JJF1638-2017 (National Standard of China)",
};

// ── Convención de signo del ERROR ───────────────────────────────────────────
// Error del instrumento = instrumento − patrón (definición estándar; es lo que
// el propio certificado escribe). El Excel calculaba patrón − instrumento, que
// tiene el signo invertido: acá lo dejamos correcto en un solo lugar.
export function instrumentError(patternValue, instrumentValue) {
  if (!isFiniteNum(patternValue) || !isFiniteNum(instrumentValue)) return null;
  return instrumentValue - patternValue;
}

// ── Incertidumbre estándar del PATRÓN ───────────────────────────────────────
// Por defecto: clase de exactitud (0,05%) del valor aplicado, como distribución
// rectangular (÷√3). Si más adelante se cargan specs por función/rango del
// certificado del patrón, esta función se reemplaza por la búsqueda en tabla.
const SQRT3 = Math.sqrt(3);
export function standardStdUncertainty(value, accuracyClassPct = ELECTRICAL_STANDARD.accuracyClass) {
  if (!isFiniteNum(value)) return 0;
  const halfWidth = (Math.abs(value) * accuracyClassPct) / 100; // semiancho rectangular
  return halfWidth / SQRT3;
}

// Incertidumbre por resolución (semi-división, rectangular): (res/2)/√3.
export function resolutionStdUncertainty(resolution) {
  if (!isFiniteNum(resolution) || resolution <= 0) return 0;
  return (resolution / 2) / SQRT3;
}

// ── Parseo de valores con multiplicador ("1K", "5M", "10,01M", "500") ───────
// Devuelve { value:Number|null, raw:String, applicable:Boolean }.
// "-" o vacío = punto/función no aplicable.
const MULTIPLIERS = { p: 1e-12, n: 1e-9, µ: 1e-6, u: 1e-6, k: 1e3, K: 1e3, M: 1e6, G: 1e9 };

export function parseElectricalValue(input) {
  const raw = (input == null ? "" : String(input)).trim();
  if (raw === "" || raw === "-" || raw === "—") {
    return { value: null, raw, applicable: false };
  }
  // normalizar: coma decimal -> punto, quitar unidad Ω/ohm y espacios
  let s = raw.replace(",", ".").replace(/\s+/g, "").replace(/(Ω|ohm|ohms)$/i, "");
  const m = s.match(/^(-?\d*\.?\d+)\s*([pnµukKMG])?$/);
  if (!m) return { value: NaN, raw, applicable: true }; // texto no numérico
  const num = parseFloat(m[1]);
  const mult = m[2] ? MULTIPLIERS[m[2]] ?? 1 : 1;
  return { value: num * mult, raw, applicable: true };
}

// Formateo compacto inverso (para mostrar valores de resistencia con sufijo).
export function formatElectricalValue(value, { multiplier = false } = {}) {
  if (!isFiniteNum(value)) return "-";
  if (!multiplier) return String(value);
  const abs = Math.abs(value);
  if (abs >= 1e6) return trim(value / 1e6) + "M";
  if (abs >= 1e3) return trim(value / 1e3) + "K";
  return trim(value);
}

// ── Helpers para armar los bloques por defecto de una sesión ────────────────
export function buildDefaultElectricalBlocks(pointCount = MULTIMETER_TEMPLATE.defaultPointCount) {
  return ELECTRICAL_FUNCTIONS.map((f, i) => ({
    block_key: f.key,
    display_name: f.label,
    unit: f.unitLabel,
    applicable: true,      // el técnico marca "no aplica" en las que no calibra
    point_count: pointCount,
    sort_order: i,
    mode: "simple",
  }));
}

function isFiniteNum(v) {
  return typeof v === "number" && Number.isFinite(v);
}
function trim(n) {
  return String(Math.round(n * 1000) / 1000);
}
